import { t } from "./i18n";

export type LogLevel = "TRACE" | "DEBUG" | "INFO" | "WARN" | "ERROR" | "FATAL";

export const LEVELS: LogLevel[] = ["TRACE", "DEBUG", "INFO", "WARN", "ERROR", "FATAL"];

export type LogEntry = {
  id?: number;
  client_id: string;
  level: LogLevel;
  message: string;
  timestamp: string;
  metadata?: string | null;
  service?: string | null;
  host?: string | null;
  trace_id?: string | null;
  span_id?: string | null;
  /** Angehängtes Payload (Dump/Bild/Video/Audio) – Storage-Dateiname */
  payload_path?: string | null;
  payload_mime?: string | null;
  payload_size?: number | null;
};

export type LogResponse = {
  logs: LogEntry[];
  total: number;
};

export type LogStatsResponse = {
  total: number;
  errors: number;
  warnings: number;
};

export type LevelMode = "minimum" | "exact";

/** Zeitraum-Presets für die Log-Suche. "custom" nutzt from/to. */
export type TimeRange = "15m" | "1h" | "6h" | "24h" | "7d" | "custom";

export const TIME_RANGES: TimeRange[] = ["15m", "1h", "6h", "24h", "7d", "custom"];

export type LogFilters = {
  level?: LogLevel;
  levelMode?: LevelMode;
  client?: string;
  service?: string;
  host?: string;
  traceId?: string;
  search?: string;
  limit?: number;
  /**
   * Gesetzt → Zeitraum-Suche über REST statt Live-Stream.
   * Presets rechnen rollierend ab jetzt; "custom" nutzt from/to.
   */
  range?: TimeRange;
  /** datetime-local Wert (YYYY-MM-DDTHH:mm), nur bei range="custom". */
  from?: string;
  to?: string;
};

export const EMPTY_FILTERS: LogFilters = {
  levelMode: "minimum",
  limit: 100,
};

export type SavedView = {
  id: string;
  name: string;
  filters: LogFilters;
  createdAt: string;
};

export type LoginResponse = {
  success: boolean;
  token?: string | null;
  message: string;
};

export type GenericResponse = {
  success: boolean;
  message: string;
};

export type ClientCreateRequest = {
  client_id: string;
  api_key: string;
};

export type QrMaintConfig = {
  enabled: boolean;
  secret: string;
  client_id: string;
  warn_status_ids: number[];
  error_status_ids: number[];
};

export const DEFAULT_QRMAINT_CONFIG: QrMaintConfig = {
  enabled: true,
  secret: "",
  client_id: "qrmaint",
  warn_status_ids: [],
  error_status_ids: [],
};

export type MetabaseSettings = {
  enabled: boolean;
  url: string;
};

export type SyslogSettings = {
  enabled: boolean;
  port: number;
  client_id: string;
  allowed_ips: string[];
};

export const DEFAULT_SYSLOG_SETTINGS: SyslogSettings = {
  enabled: false,
  port: 5514,
  client_id: "syslog",
  allowed_ips: [],
};

export type MqttSettings = {
  enabled: boolean;
  port: number;
  allow_anonymous: boolean;
  anonymous_client_id: string;
};

export const DEFAULT_MQTT_SETTINGS: MqttSettings = {
  enabled: false,
  port: 1883,
  allow_anonymous: false,
  anonymous_client_id: "mqtt",
};

export const DEFAULT_METABASE_SETTINGS: MetabaseSettings = {
  enabled: false,
  url: "",
};

export type UserRole = "admin" | "viewer";

export type UserWithRole = {
  username: string;
  role: UserRole;
};

export type UserCreateRequest = {
  username: string;
  password: string;
  role: UserRole;
};

export type UpdateUserRoleRequest = {
  role: UserRole;
};

export type LicenseStatusCode = "full" | "grace" | "read_only" | "unlicensed";

export type LicenseStatus = {
  status: LicenseStatusCode;
  allows_ingest: boolean;
  needs_warning: boolean;
  installed: boolean;
  customer: string | null;
  license_id: string | null;
  issued_at: string | null;
  valid_until: string | null;
  max_clients: number | null;
  current_clients: number;
  features: string[];
  days_remaining: number | null;
};

export type CleanupSettings = {
  enabled: boolean;
  retention_days: number;
  cleanup_hour: number;
};

export const DEFAULT_CLEANUP_SETTINGS: CleanupSettings = {
  enabled: false,
  retention_days: 30,
  cleanup_hour: 3,
};

export type PayloadSettings = {
  /** Maximale Größe eines an ein Log-Event angehängten Payloads (Bytes) */
  max_bytes: number;
};

export const DEFAULT_PAYLOAD_SETTINGS: PayloadSettings = {
  max_bytes: 25 * 1024 * 1024,
};

export type VersionResponse = { current: string };

export type UpdateCheckResponse = {
  current: string;
  latest: string | null;
  update_available: boolean;
  error: string | null;
  trigger_enabled: boolean;
};

/**
 * Status-Phasen vom log-updater Sidecar, gespiegelt aus
 * tools/log_updater.rs::Phase. Der Backend-Proxy ergänzt `unreachable`,
 * `unavailable` und `error`, wenn der Sidecar nicht erreichbar / nicht
 * konfiguriert ist.
 */
export type UpdatePhase =
  | { phase: "idle" }
  | { phase: "pulling"; started_at: string }
  | { phase: "recreating"; started_at: string }
  | { phase: "ok"; started_at: string; finished_at: string }
  | { phase: "failed"; started_at: string; finished_at: string; reason: string }
  | { phase: "unreachable"; reason: string }
  | { phase: "unavailable"; reason: string }
  | { phase: "error"; reason: string };

// ── Facility (Anlagen) ─────────────────────────────────────────────────

export type FacilityMap = {
  id: number;
  name: string;
  image_path: string;
  width_px: number | null;
  height_px: number | null;
  created_at: string;
};

export type MarkerShape = "point" | "rect" | "polygon";

export type FacilityMarker = {
  id: number;
  map_id: number;
  client_id: string | null;
  service: string | null;
  host: string | null;
  x_pct: number;
  y_pct: number;
  label: string | null;
  stale_after_minutes: number;
  shape: MarkerShape;
  geometry: string | null;
};

/** `idle` = Quelle lebt, Programm läuft nicht (nur aus dem Zellzustand einer GreenBox). */
export type MarkerStatusCode = "ok" | "idle" | "warn" | "error" | "stale";

export type MarkerStatusEntry = {
  id: number;
  label: string | null;
  client_id: string | null;
  service: string | null;
  host: string | null;
  x_pct: number;
  y_pct: number;
  shape: MarkerShape;
  geometry: string | null;
  status: MarkerStatusCode;
  last_message: string | null;
  last_timestamp: string | null;
};

export type FacilityStatusPayload = {
  markers: MarkerStatusEntry[];
  counts: Partial<Record<MarkerStatusCode, number>>;
};

export type ShowMode = {
  id: number;
  name: string;
  /** JSON-Array der aktiven Feature-Keys */
  features: string;
  header_title: string | null;
  header_subtitle: string | null;
  footer_text: string | null;
  /** Mindest-Level für den Event-Feed (null = alle) */
  min_level: LogLevel | null;
  created_at: string;
};

// ── Marker-Geometrie ──────────────────────────────────────────────────
// rect:    { w, h } in %, oben-links = x_pct/y_pct
// polygon: { points: [[x,y], …] } in %, erster Punkt = x_pct/y_pct

export type RectGeometry = { w: number; h: number };
export type PolygonGeometry = { points: [number, number][] };

export function parseRectGeometry(raw: string | null | undefined): RectGeometry | null {
  if (!raw) return null;
  try {
    const obj = JSON.parse(raw);
    const w = Number(obj.w);
    const h = Number(obj.h);
    if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) return { w, h };
  } catch {
    // ignore
  }
  return null;
}

export function parsePolygonGeometry(raw: string | null | undefined): PolygonGeometry | null {
  if (!raw) return null;
  try {
    const obj = JSON.parse(raw);
    if (!Array.isArray(obj.points)) return null;
    const pts: [number, number][] = [];
    for (const p of obj.points) {
      if (!Array.isArray(p) || p.length !== 2) continue;
      const x = Number(p[0]);
      const y = Number(p[1]);
      if (Number.isFinite(x) && Number.isFinite(y)) pts.push([x, y]);
    }
    return pts.length >= 3 ? { points: pts } : null;
  } catch {
    return null;
  }
}

export const SHOW_MODE_FEATURES = [
  { key: "live_clock", label: t("Live-Uhr") },
  { key: "tiles", label: t("Kennzahlen-Tiles") },
  { key: "event_feed", label: t("Event-Feed") },
  { key: "marker_pulse", label: t("Marker-Puls") },
  { key: "crossfade", label: t("Crossfade-Übergänge") },
  { key: "custom_header", label: t("Eigener Header") },
  { key: "footer_brand", label: t("Footer-Watermark") },
];

/** Liest die `features`-Spalte (JSON-Array) und prüft, ob ein Feature aktiv ist. */
export function hasFeature(mode: ShowMode | null, key: string): boolean {
  if (!mode) return false;
  try {
    const arr = JSON.parse(mode.features);
    return Array.isArray(arr) && arr.includes(key);
  } catch {
    return false;
  }
}

// ── Alarmierung (Teams) ───────────────────────────────────────────────

export type AlertChannelType = "teams" | "webhook" | "email" | "nextcloud_talk";

export type TeamsAlert = {
  name: string;
  webhook_url: string;
  patterns: string[];
  levels: string[];
  enabled: boolean;
  /** Transport: Teams-Karte, generisches JSON-POST oder E-Mail (SMTP). */
  channel_type: AlertChannelType;
  /** Kommagetrennte Empfänger für channel_type = "email". */
  email_to: string;
  /** Bot-Geheimnis für channel_type = "nextcloud_talk"; der Link zur Unterhaltung steht in webhook_url. */
  talk_secret?: string;
  /** Sturm-Fenster: gleiche Meldung (normalisiert) je Client N Sek. nach dem letzten Auftreten zusammenfassen (0/null = aus) */
  collapse_window_sec?: number | null;
  /** Max. Meldungen pro Minute je Regel (0/null = unbegrenzt) */
  max_per_minute?: number | null;
  /** Erst senden, wenn der Fehler nach N Sek. noch anhält (0/null = sofort) */
  min_pending_sec?: number | null;
  /** Abschlussmeldung bei Sturmende ("×247 in 4m12s") */
  summary_on_storm_end?: boolean;
  /** @deprecated Legacy – serverseitig auf collapse_window_sec gemappt */
  dedupe_window_sec?: number | null;
  /** @deprecated Legacy – serverseitig auf max_per_minute (≈ 60/cooldown) gemappt */
  cooldown_sec?: number | null;
};

/** Spiegelt `TeamsAlert::effective_*` im Backend, damit alte Configs im Formular korrekt erscheinen. */
export function effectiveCollapseWindow(a: TeamsAlert): number {
  return a.collapse_window_sec ?? a.dedupe_window_sec ?? 0;
}
export function effectiveMaxPerMinute(a: TeamsAlert): number {
  if (a.max_per_minute != null) return a.max_per_minute;
  const c = a.cooldown_sec ?? 0;
  return c > 0 ? Math.max(1, Math.floor(60 / c)) : 0;
}

export type TeamsAlertsConfig = {
  alerts: TeamsAlert[];
};

// ── Single Sign-On (OIDC) ─────────────────────────────────────────────

export type OidcConfig = {
  enabled: boolean;
  issuer_url: string;
  client_id: string;
  /** Beim Laden immer leer; leer lassen = bestehendes Secret behalten. */
  client_secret: string;
  scopes: string;
  groups_claim: string;
  admin_groups: string[];
  auto_provision: boolean;
  default_role: "admin" | "viewer";
  redirect_base_url: string;
  button_label: string;
  /** Nur in der GET-Antwort: ob serverseitig ein Secret hinterlegt ist. */
  has_client_secret?: boolean;
};

export const EMPTY_OIDC_CONFIG: OidcConfig = {
  enabled: false,
  issuer_url: "",
  client_id: "",
  client_secret: "",
  scopes: "openid email profile",
  groups_claim: "groups",
  admin_groups: [],
  auto_provision: true,
  default_role: "viewer",
  redirect_base_url: "",
  button_label: t("Mit SSO anmelden"),
};

/** Öffentlicher Status für den Login-Screen (ob der SSO-Button erscheint). */
export type OidcPublicStatus = {
  enabled: boolean;
  button_label: string;
};

export const EMPTY_TEAMS_ALERT: TeamsAlert = {
  name: "",
  webhook_url: "",
  patterns: [],
  levels: ["ANY"],
  enabled: true,
  channel_type: "teams",
  email_to: "",
  collapse_window_sec: 60,
  max_per_minute: 10,
  min_pending_sec: null,
  summary_on_storm_end: false,
};

export type SmtpSettings = {
  host: string;
  port: number;
  username: string;
  password: string;
  from: string;
  security: "starttls" | "tls" | "none";
};

export const DEFAULT_SMTP_SETTINGS: SmtpSettings = {
  host: "",
  port: 587,
  username: "",
  password: "",
  from: "",
  security: "starttls",
};

export const ALERT_LEVELS: ("ANY" | LogLevel)[] = [
  "ANY",
  "TRACE",
  "DEBUG",
  "INFO",
  "WARN",
  "ERROR",
  "FATAL",
];

// ── Ingest-Health (Overview/Übersicht) ────────────────────────────────

export type IngestHealth = {
  received: number;
  flushed: number;
  dropped: number;
  batches: number;
  last_batch_size: number;
  queue_capacity: number;
  queue_used_pct: number;
};

// ── Bericht (Auffälligkeiten als Text, geplant oder auf Knopfdruck) ───

export type Schwere = "hinweis" | "warnung" | "dringend";

export type Befund = {
  art: string;
  schwere: Schwere;
  client: string | null;
  /** Eine Zeile, die für sich steht. */
  titel: string;
  /** Die Zahl mit ihrem Maßstab („47 Fehler, erwartbar wären 12"). */
  zahl: string;
  handlung: string;
  link?: string | null;
  schluessel: string;
  /** Datum des ersten Auftretens. Fehlt = heute neu. */
  seit?: string | null;
  /** Aus der Lernphase: noch zu wenige Tage für ein belastbares Urteil. */
  vorlaeufig?: boolean;
  /** Wiederkehrendes Muster, das noch niemand eingestuft hat. */
  frage?: boolean;
};

/** Ein als normal bestätigtes Muster, das im Zeitraum wie gewohnt auftrat. */
export type BerichtBekannt = {
  schluessel: string;
  client: string;
  text: string;
  grund: string;
};

export type BerichtLernstand = {
  client: string;
  tage: number;
  noetig: number;
};

/** Eine gespeicherte Einstufung (Liste unter „Eingestufte Muster"). */
export type MusterEinstufung = {
  schluessel: string;
  status: "normal" | "unnormal" | "stumm";
  grund: string;
  beschreibung: string;
  entschieden: string;
  stumm_bis?: string | null;
  /** Wirkt heute noch. Falsch = abgelaufen, wird wieder vorgelegt. */
  gilt: boolean;
};

export type BerichtRaster = {
  client: string;
  groesse: "zyklen" | "meldungen" | "fehler";
  tage: { tag: string; werktag: boolean; werte: (number | null)[] }[];
};

export type BerichtZeile = {
  client: string;
  logs: number;
  fehler: number;
  warnungen: number;
  stoerfaelle: number;
  /** Fachliche Kennzahlen der Anlage als [Name, Wert]. */
  kennzahlen: [string, number][];
};

/** Eine Stunde im Diagramm „Zeitraum gegen üblich“. */
export type BerichtStunde = {
  /** Kalendertag in Ortszeit, `TT.MM.`. */
  tag: string;
  stunde: number;
  ist: number | null;
  /** Median derselben Stunde an den Vortagen. */
  ueblich: number | null;
  auffaellig: boolean;
};

/** Die Zahlen hinter den Diagrammen einer Anlage. */
export type BerichtBild = {
  client: string;
  groesse: "zyklen" | "meldungen" | "fehler";
  stunden: BerichtStunde[];
};

export type Bericht = {
  /** Kennung im Archiv. Fehlt bei Berichten aus der Zeit davor. */
  id?: string;
  erzeugt: string;
  von: string;
  bis: string;
  anlass: "taeglich" | "woechentlich" | "sofort";
  befunde: Befund[];
  zeilen: BerichtZeile[];
  einordnung?: string | null;
  hinweise: string[];
  bekannt?: BerichtBekannt[];
  lernstand?: BerichtLernstand[];
  bilder?: BerichtBild[];
};

export type BerichtJetzt = {
  bericht: Bericht;
  gesendet: boolean;
  /** Derselbe Bericht als Klartext, wie er per E-Mail rausgeht. */
  text: string;
};

export type BerichtEinstellungen = {
  enabled: boolean;
  taeglich_um: string[];
  woechentlich_am: number | null;
  woechentlich_um: string;
  grundlinie_tage: number;
  clients: string[];
  /** Namen der Kanäle aus der Alarmierung, über die der Bericht rausgeht. */
  kanaele: string[];
  nur_bei_befund: boolean;
  ki_text: boolean;
  /** Fragen an die Daten auf der Seite Berichte erlauben. */
  fragen: boolean;
  min_schwere: Schwere;
  basis_url: string;
};

export const DEFAULT_BERICHT_EINSTELLUNGEN: BerichtEinstellungen = {
  enabled: false,
  taeglich_um: ["07:00"],
  woechentlich_am: null,
  woechentlich_um: "07:30",
  grundlinie_tage: 14,
  clients: [],
  kanaele: [],
  nur_bei_befund: true,
  ki_text: false,
  fragen: false,
  min_schwere: "hinweis",
  basis_url: "",
};

/** Ob die Seite Berichte Fragen an die Daten anbietet. */
export type FrageStatus = { aktiv: boolean; lizenz: boolean };

/** Eine Abfrage, die Leif für eine Frage ausgeführt hat. */
export type FrageGrundlage = {
  werkzeug: string;
  /** Eine Zeile: was gezählt wurde und wie viel. */
  kurz: string;
  daten: Record<string, unknown>;
};

export type FrageAntwort = {
  antwort: string;
  grundlage: FrageGrundlage[];
  /** Zahlen der Antwort, die in keinem Ergebnis stehen. */
  ungedeckt?: string[];
  fehler?: string;
  schritte: number;
  kosten_cent?: number;
  guthaben_cent?: number;
};
